import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Palette,
  Type,
  Image,
  Save,
  RefreshCw,
  Check,
  AlertCircle,
  Eye,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Star,
  Zap,
  Trophy,
} from 'lucide-react';
import { api } from '../../services/api';
import { Tooltip } from '../../components/Tooltip';
import { generateFullTheme, isValidHexColor } from './utils/colorGenerator';
import { ColorWizard } from './components/ColorWizard';

interface WebsiteImage {
  key: string;
  url: string;
  alt_text: string;
}

// Group content by section
const SECTIONS = [
  { id: 'hero', name: 'Hero Section' },
  { id: 'features', name: 'Features' },
  { id: 'how_it_works', name: 'How It Works' },
  { id: 'pricing', name: 'Pricing' },
  { id: 'download', name: 'Download' },
  { id: 'footer', name: 'Footer' },
];

export default function AdminWebsite() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'content' | 'theme' | 'images'>('content');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['hero']));
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Pending changes tracking
  const [pendingContentChanges, setPendingContentChanges] = useState<Record<string, string>>({});
  const [pendingThemeChanges, setPendingThemeChanges] = useState<Record<string, string>>({});

  // Auto-generate theme state
  const [autoGenBaseColor, setAutoGenBaseColor] = useState('#2e8b57');
  const [showAutoGenPreview, setShowAutoGenPreview] = useState(false);
  const [autoGenPreviewColors, setAutoGenPreviewColors] = useState<Record<string, string>>({});
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardKey, setWizardKey] = useState(0);

  // Fetch content
  const { data: contentData, isLoading: contentLoading } = useQuery({
    queryKey: ['admin-website-content'],
    queryFn: () => api.getAdminWebsiteContent(),
  });

  // Fetch theme
  const { data: themeData, isLoading: themeLoading } = useQuery({
    queryKey: ['admin-website-theme'],
    queryFn: () => api.getAdminWebsiteTheme(),
  });

  // Fetch images
  const { data: imagesData } = useQuery({
    queryKey: ['admin-website-images'],
    queryFn: () => api.getAdminWebsiteImages(),
  });

  const content = contentData?.content || {};
  const theme = themeData?.theme || {};
  const images: WebsiteImage[] = imagesData?.images || [];

  // Save content mutation
  const saveContentMutation = useMutation({
    mutationFn: (updates: { section: string; key: string; value: string }[]) =>
      api.updateAdminWebsiteContentBulk({ updates }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-website-content'] });
      setPendingContentChanges({});
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    },
    onError: () => {
      setSaveStatus('error');
    },
  });

  // Save theme mutation
  const saveThemeMutation = useMutation({
    mutationFn: (themeUpdates: Record<string, string>) =>
      api.updateAdminWebsiteThemeBulk({ theme: themeUpdates }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-website-theme'] });
      setPendingThemeChanges({});
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    },
    onError: () => {
      setSaveStatus('error');
    },
  });

  const handleContentChange = (fullKey: string, value: string) => {
    setPendingContentChanges((prev) => ({ ...prev, [fullKey]: value }));
  };

  const handleThemeChange = (key: string, value: string) => {
    setPendingThemeChanges((prev) => ({ ...prev, [key]: value }));
  };

  const saveContentChanges = () => {
    if (Object.keys(pendingContentChanges).length === 0) return;

    const updates = Object.entries(pendingContentChanges).map(([fullKey, value]) => {
      const [section, ...keyParts] = fullKey.split('.');
      return { section, key: keyParts.join('.'), value };
    });

    saveContentMutation.mutate(updates);
  };

  const saveThemeChanges = () => {
    if (Object.keys(pendingThemeChanges).length === 0) return;
    saveThemeMutation.mutate(pendingThemeChanges);
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const getContentValue = (fullKey: string) => {
    return pendingContentChanges[fullKey] ?? content[fullKey] ?? '';
  };

  const getThemeValue = (key: string) => {
    return pendingThemeChanges[key] ?? theme[key] ?? '';
  };

  // Auto-generate theme functions
  const handleAutoGenPreview = () => {
    if (!isValidHexColor(autoGenBaseColor)) return;
    const generated = generateFullTheme(autoGenBaseColor);
    setAutoGenPreviewColors(generated);
    setShowAutoGenPreview(true);
  };

  const handleAutoGenApply = () => {
    setPendingThemeChanges((prev) => ({ ...prev, ...autoGenPreviewColors }));
    setShowAutoGenPreview(false);
  };

  const handleWizardApply = (theme: Record<string, string>) => {
    setPendingThemeChanges((prev) => ({ ...prev, ...theme }));
  };

  const hasUnsavedChanges =
    Object.keys(pendingContentChanges).length > 0 || Object.keys(pendingThemeChanges).length > 0;

  const isLoading = contentLoading || themeLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Website CMS</h1>
          <p className="text-gray-600">
            Manage website content, theme colors, and images
          </p>
        </div>
        <div className="flex items-center gap-4">
          {hasUnsavedChanges && (
            <span className="text-sm text-amber-600">
              Unsaved changes
            </span>
          )}
          <a
            href="https://rumio.world"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Eye className="w-4 h-4" />
            Preview Site
          </a>
        </div>
      </div>

      {/* Save status */}
      {saveStatus === 'success' && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          <Check className="w-5 h-5" />
          Changes saved successfully
        </div>
      )}

      {saveStatus === 'error' && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-5 h-5" />
          Failed to save changes
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('content')}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium transition-colors ${
              activeTab === 'content'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Type className="w-4 h-4" />
            Content
          </button>
          <button
            onClick={() => setActiveTab('theme')}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium transition-colors ${
              activeTab === 'theme'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Palette className="w-4 h-4" />
            Theme
          </button>
          <button
            onClick={() => setActiveTab('images')}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium transition-colors ${
              activeTab === 'images'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Image className="w-4 h-4" />
            Images
          </button>
        </nav>
      </div>

      {/* Content Tab */}
      {activeTab === 'content' && (
        <div className="space-y-4">
          {SECTIONS.map((section) => {
            const sectionKeys = Object.keys(content).filter((k) =>
              k.startsWith(`${section.id}.`)
            );
            const isExpanded = expandedSections.has(section.id);

            return (
              <div
                key={section.id}
                className="bg-white rounded-lg shadow border border-gray-200"
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between p-4 text-left"
                >
                  <h3 className="font-semibold text-gray-900">
                    {section.name}
                  </h3>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>

                {isExpanded && (
                  <div className="p-4 pt-0 space-y-4 border-t border-gray-100">
                    {sectionKeys.map((fullKey) => {
                      const label = fullKey.split('.').slice(1).join('.').replace(/_/g, ' ');
                      const value = getContentValue(fullKey);
                      const isModified = pendingContentChanges[fullKey] !== undefined;

                      return (
                        <div key={fullKey}>
                          <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                            {label}
                            {isModified && (
                              <span className="ml-2 text-xs text-amber-600">Modified</span>
                            )}
                          </label>
                          {value.length > 100 ? (
                            <textarea
                              value={value}
                              onChange={(e) => handleContentChange(fullKey, e.target.value)}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-primary-500"
                            />
                          ) : (
                            <input
                              type="text"
                              value={value}
                              onChange={(e) => handleContentChange(fullKey, e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-primary-500"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {Object.keys(pendingContentChanges).length > 0 && (
            <button
              onClick={saveContentChanges}
              disabled={saveContentMutation.isPending}
              className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {saveContentMutation.isPending ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Content Changes
            </button>
          )}
        </div>
      )}

      {/* Theme Tab */}
      {activeTab === 'theme' && (
        <div className="space-y-6">
          {/* Auto-Generate Card */}
          <div className="bg-gradient-to-r from-primary-50 to-white rounded-lg shadow border border-primary-200 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary-600" />
                  Auto-Generate Theme
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Generate a complete color palette from your brand color
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={autoGenBaseColor}
                    onChange={(e) => setAutoGenBaseColor(e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                  />
                  <input
                    type="text"
                    value={autoGenBaseColor}
                    onChange={(e) => setAutoGenBaseColor(e.target.value)}
                    className="w-24 px-2 py-1 text-sm font-mono border border-gray-300 rounded bg-white text-gray-900"
                  />
                </div>

                <button
                  onClick={handleAutoGenPreview}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                >
                  Quick Generate
                </button>
                <button
                  onClick={() => {
                    setWizardKey(k => k + 1);
                    setWizardOpen(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all text-sm font-medium shadow-md"
                >
                  <Sparkles className="w-4 h-4" />
                  Open Wizard
                </button>
              </div>
            </div>

            {/* Preview Panel */}
            {showAutoGenPreview && (
              <div className="mt-4 pt-4 border-t border-primary-200">
                <p className="text-sm text-gray-600 mb-3">Generated palette preview:</p>
                <div className="grid grid-cols-5 sm:grid-cols-10 gap-1 mb-4">
                  {Object.entries(autoGenPreviewColors)
                    .filter(([key]) => key.startsWith('primary_'))
                    .sort(([a], [b]) => parseInt(a.split('_')[1]) - parseInt(b.split('_')[1]))
                    .map(([key, value]) => (
                      <div key={key} className="text-center">
                        <div
                          className="w-full h-8 rounded border border-gray-200"
                          style={{ backgroundColor: value }}
                          title={`${key}: ${value}`}
                        />
                        <span className="text-[10px] text-gray-500">{key.split('_')[1]}</span>
                      </div>
                    ))}
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {['accent', 'action_light', 'action', 'action_dark'].map((key) => (
                    <div key={key} className="flex items-center gap-1">
                      <div
                        className="w-6 h-6 rounded border border-gray-200"
                        style={{ backgroundColor: autoGenPreviewColors[key] }}
                      />
                      <span className="text-xs text-gray-600">{key.replace('_', ' ')}</span>
                    </div>
                  ))}
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setShowAutoGenPreview(false)}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAutoGenApply}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                  >
                    Apply All Colors
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Primary Colors */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-gray-900">Primary Brand Colors</h3>
              <Tooltip text="Main brand colors used for buttons, links, and key UI elements throughout the app" />
            </div>
            <p className="text-sm text-gray-600 mb-4">10-shade palette from light to dark</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
              {['primary_50', 'primary_100', 'primary_200', 'primary_300', 'primary_400', 'primary_500', 'primary_600', 'primary_700', 'primary_800', 'primary_900'].map(
                (key) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {key.replace('primary_', '')}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={getThemeValue(key) || '#ffffff'}
                        onChange={(e) => handleThemeChange(key, e.target.value)}
                        className="w-10 h-10 rounded cursor-pointer border border-gray-200 flex-shrink-0"
                      />
                      <input
                        type="text"
                        value={getThemeValue(key)}
                        onChange={(e) => handleThemeChange(key, e.target.value)}
                        className="flex-1 min-w-0 px-2 py-1 text-sm border border-gray-300 rounded bg-white text-gray-900 font-mono"
                      />
                    </div>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Action & Highlight Colors */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-5 h-5 text-amber-500" />
              <h3 className="font-semibold text-gray-900">Action & Highlight Colors</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">Used for CTAs, buttons, and attention-grabbing elements</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { key: 'action_light', label: 'Action Light', tooltip: 'Lighter gradient stop for CTA buttons' },
                { key: 'action', label: 'Action', tooltip: 'Primary action color for call-to-action buttons' },
                { key: 'action_dark', label: 'Action Dark', tooltip: 'Darker shade for hover states and gradient endpoints' },
              ].map(({ key, label, tooltip }) => (
                <div key={key}>
                  <div className="flex items-center gap-1 mb-1">
                    <label className="block text-xs font-medium text-gray-600">{label}</label>
                    <Tooltip text={tooltip} />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={getThemeValue(key) || '#f59e0b'}
                      onChange={(e) => handleThemeChange(key, e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer border border-gray-200 flex-shrink-0"
                    />
                    <input
                      type="text"
                      value={getThemeValue(key)}
                      onChange={(e) => handleThemeChange(key, e.target.value)}
                      className="flex-1 min-w-0 px-2 py-1 text-sm border border-gray-300 rounded bg-white text-gray-900 font-mono"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Accent Colors */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-gray-900">Accent Colors</h3>
              <Tooltip text="Secondary brand colors used for accents and highlights" />
            </div>
            <p className="text-sm text-gray-600 mb-4">Secondary brand colors for visual variety</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { key: 'accent', label: 'Accent', tooltip: 'Secondary brand color for accents and decorative elements' },
                { key: 'accent_dark', label: 'Accent Dark', tooltip: 'Darker accent for hover states' },
              ].map(({ key, label, tooltip }) => (
                <div key={key}>
                  <div className="flex items-center gap-1 mb-1">
                    <label className="block text-xs font-medium text-gray-600">{label}</label>
                    <Tooltip text={tooltip} />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={getThemeValue(key) || '#d4af37'}
                      onChange={(e) => handleThemeChange(key, e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer border border-gray-200 flex-shrink-0"
                    />
                    <input
                      type="text"
                      value={getThemeValue(key)}
                      onChange={(e) => handleThemeChange(key, e.target.value)}
                      className="flex-1 min-w-0 px-2 py-1 text-sm border border-gray-300 rounded bg-white text-gray-900 font-mono"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Gamification & Tiers */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-1">
              <Trophy className="w-5 h-5 text-yellow-500" />
              <h3 className="font-semibold text-gray-900">Gamification & Tiers</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">Colors for tier badges, ratings, and rewards</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { key: 'star_rating', label: 'Star Rating', tooltip: 'Star rating icons in reviews and marketplace', icon: Star },
                { key: 'tier_bronze', label: 'Bronze Tier', tooltip: 'Bronze tier badge color in gamification' },
                { key: 'tier_gold', label: 'Gold Tier', tooltip: 'Gold tier badge color in gamification' },
                { key: 'tier_super', label: 'Super Tier', tooltip: 'Super tier color for Rumios currency' },
              ].map(({ key, label, tooltip, icon: Icon }) => (
                <div key={key}>
                  <div className="flex items-center gap-1 mb-1">
                    {Icon && <Icon className="w-3 h-3 text-yellow-500" />}
                    <label className="block text-xs font-medium text-gray-600">{label}</label>
                    <Tooltip text={tooltip} />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={getThemeValue(key) || '#facc15'}
                      onChange={(e) => handleThemeChange(key, e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer border border-gray-200 flex-shrink-0"
                    />
                    <input
                      type="text"
                      value={getThemeValue(key)}
                      onChange={(e) => handleThemeChange(key, e.target.value)}
                      className="flex-1 min-w-0 px-2 py-1 text-sm border border-gray-300 rounded bg-white text-gray-900 font-mono"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Logo Colors */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-gray-900">Logo Colors</h3>
              <Tooltip text="RUMI text color in the logo (golden ring is fixed)" />
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Customize the RUMI text color. The golden ring remains fixed.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { key: 'logo_text_color', label: 'Text Color (Light BG)', tooltip: 'RUMI text color on light backgrounds', defaultVal: '#2E8B57' },
                { key: 'logo_text_color_light', label: 'Text Color (Dark BG)', tooltip: 'RUMI text color on dark backgrounds', defaultVal: '#FFFFFF' },
              ].map(({ key, label, tooltip, defaultVal }) => (
                <div key={key}>
                  <div className="flex items-center gap-1 mb-1">
                    <label className="block text-xs font-medium text-gray-600">{label}</label>
                    <Tooltip text={tooltip} />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={getThemeValue(key) || defaultVal}
                      onChange={(e) => handleThemeChange(key, e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer border border-gray-200 flex-shrink-0"
                    />
                    <input
                      type="text"
                      value={getThemeValue(key) || defaultVal}
                      onChange={(e) => handleThemeChange(key, e.target.value)}
                      placeholder={defaultVal}
                      className="flex-1 min-w-0 px-2 py-1 text-sm border border-gray-300 rounded bg-white text-gray-900 font-mono"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Logo Preview - stacks vertically on mobile */}
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Logo Preview</h4>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                {/* Light background preview */}
                <div className="p-4 sm:p-6 bg-gray-50 rounded-lg border border-gray-200 flex-1 flex justify-center">
                  <svg viewBox="0 0 160 50" className="h-10 sm:h-12" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id="goldRingPreview" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#FFE066"/>
                        <stop offset="25%" stopColor="#D4AF37"/>
                        <stop offset="50%" stopColor="#B8860B"/>
                        <stop offset="75%" stopColor="#D4AF37"/>
                        <stop offset="100%" stopColor="#996515"/>
                      </linearGradient>
                    </defs>
                    <text x="8" y="35" fontFamily="Verdana, sans-serif" fontSize="32" fontWeight="bold" fill={getThemeValue('logo_text_color') || '#2E8B57'}>RUMI</text>
                    <g transform="translate(125, 25)">
                      <circle cx="0" cy="0" r="14" fill="none" stroke="url(#goldRingPreview)" strokeWidth="5"/>
                    </g>
                  </svg>
                </div>
                {/* Dark background preview */}
                <div className="p-4 sm:p-6 bg-gray-800 rounded-lg flex-1 flex justify-center">
                  <svg viewBox="0 0 160 50" className="h-10 sm:h-12" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id="goldRingPreviewDark" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#FFE066"/>
                        <stop offset="25%" stopColor="#D4AF37"/>
                        <stop offset="50%" stopColor="#B8860B"/>
                        <stop offset="75%" stopColor="#D4AF37"/>
                        <stop offset="100%" stopColor="#996515"/>
                      </linearGradient>
                    </defs>
                    <text x="8" y="35" fontFamily="Verdana, sans-serif" fontSize="32" fontWeight="bold" fill={getThemeValue('logo_text_color_light') || '#FFFFFF'}>RUMI</text>
                    <g transform="translate(125, 25)">
                      <circle cx="0" cy="0" r="14" fill="none" stroke="url(#goldRingPreviewDark)" strokeWidth="5"/>
                    </g>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Theme Preview */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-4 sm:p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Preview</h3>
            <div className="flex flex-wrap gap-3 sm:gap-4">
              <button
                type="button"
                style={{ backgroundColor: getThemeValue('primary_600') || '#2e8b57' }}
                className="px-4 py-2 text-white rounded-lg text-sm"
              >
                Primary Button
              </button>
              <button
                type="button"
                style={{
                  background: `linear-gradient(to right, ${getThemeValue('action_light') || '#fbbf24'}, ${getThemeValue('action_dark') || '#ea580c'})`,
                }}
                className="px-4 py-2 text-white rounded-lg text-sm font-medium"
              >
                Action Button
              </button>
              <button
                type="button"
                style={{ backgroundColor: getThemeValue('accent') || '#d4af37' }}
                className="px-4 py-2 text-gray-900 rounded-lg text-sm"
              >
                Accent Button
              </button>
              <div
                style={{ backgroundColor: getThemeValue('primary_100') || '#c8e6c9' }}
                className="px-4 py-2 rounded-lg"
              >
                <span style={{ color: getThemeValue('primary_700') || '#388e3c' }} className="text-sm">Light BG Text</span>
              </div>
              <div className="flex items-center gap-1 px-3 py-2 bg-gray-100 rounded-lg">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    className="w-4 h-4"
                    style={{ fill: getThemeValue('star_rating') || '#facc15', color: getThemeValue('star_rating') || '#facc15' }}
                  />
                ))}
              </div>
            </div>
          </div>

          {Object.keys(pendingThemeChanges).length > 0 && (
            <div className="sticky bottom-4 sm:relative sm:bottom-auto">
              <button
                onClick={saveThemeChanges}
                disabled={saveThemeMutation.isPending}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 sm:py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 shadow-lg sm:shadow-none"
              >
                {saveThemeMutation.isPending ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Theme Changes
              </button>
            </div>
          )}
        </div>
      )}

      {/* Images Tab */}
      {activeTab === 'images' && (
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">
              Website Images
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Image uploads coming soon. For now, update image URLs directly in the database.
            </p>

            <div className="space-y-4">
              {images.map((image) => (
                <div
                  key={image.key}
                  className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg"
                >
                  {image.url && (
                    <img
                      src={image.url}
                      alt={image.alt_text}
                      className="w-16 h-16 object-cover rounded"
                    />
                  )}
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      {image.key}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {image.url}
                    </p>
                    {image.alt_text && (
                      <p className="text-xs text-gray-400">Alt: {image.alt_text}</p>
                    )}
                  </div>
                </div>
              ))}

              {images.length === 0 && (
                <p className="text-gray-500 text-center py-8">
                  No images configured yet
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Color Wizard Modal */}
      <ColorWizard
        key={wizardKey}
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onApply={handleWizardApply}
        initialColor={autoGenBaseColor}
      />
    </div>
  );
}
